import qrCodeImage from "@assets/images/Frame@2x.png";
import { Reveal } from "@components/ui";
import type { ReactNode } from "react";

/* ─────────────────────────────────────────────────────────
   Icon Components
   ───────────────────────────────────────────────────────── */

function CreditCardIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="1" y="4" width="22" height="16" rx="3" />
      <line x1="1" y1="10" x2="23" y2="10" />
      <line x1="5" y1="15" x2="9" y2="15" />
      <line x1="12" y1="15" x2="14" y2="15" />
    </svg>
  );
}

function MeterChartIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <line x1="4" y1="20" x2="4" y2="14" />
      <line x1="9" y1="20" x2="9" y2="8" />
      <line x1="14" y1="20" x2="14" y2="11" />
      <line x1="19" y1="20" x2="19" y2="5" />
      <polyline points="4 14 9 8 14 11 19 5" />
    </svg>
  );
}

function DocumentIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="8" y1="13" x2="16" y2="13" />
      <line x1="8" y1="17" x2="13" y2="17" />
    </svg>
  );
}

function RefreshCycleIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21.5 2v6h-6" />
      <path d="M2.5 22v-6h6" />
      <path d="M2.5 12A9.5 9.5 0 0 1 6.34 4.84L21.5 8" />
      <path d="M21.5 12a9.5 9.5 0 0 1-3.84 7.16L2.5 16" />
    </svg>
  );
}

function PlugLinkIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2v4" />
      <path d="M8 2v4" />
      <path d="M16 2v4" />
      <rect x="6" y="6" width="12" height="6" rx="2" />
      <path d="M10 12v2a2 2 0 0 0 4 0v-2" />
      <line x1="12" y1="16" x2="12" y2="22" />
      <line x1="8" y1="22" x2="16" y2="22" />
    </svg>
  );
}

function HeadsetIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 18v-6a9 9 0 0 1 18 0v6" />
      <path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3v5Z" />
      <path d="M3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3v5Z" />
    </svg>
  );
}

/* ─────────────────────────────────────────────────────────
   Sub-components
   ───────────────────────────────────────────────────────── */

interface PortalFeatureProps {
  title: string;
  description: string;
  icon: ReactNode;
}

function PortalFeature({ title, description, icon }: PortalFeatureProps) {
  return (
    <div className="group bg-white hover:shadow-[0_2px_16px_rgba(0,0,0,0.08)] transition-all duration-300 border border-transparent hover:border-[#e8e9ed] rounded-[16px] p-4 md:p-5 flex flex-col items-start gap-3 md:gap-4 cursor-default">
      <div className="w-10 h-10 md:w-12 md:h-12 rounded-[12px] bg-[#f24d2b]/[0.08] text-[#f24d2b] flex items-center justify-center transition-all duration-300 group-hover:bg-[#f24d2b] group-hover:text-white">
        {icon}
      </div>
      <div className="flex flex-col gap-1 md:gap-1.5">
        <h3 className="text-sm md:text-lg lg:text-[22px] tracking-[-0.04em] font-semibold text-[#122d63] font-albert-sans">
          {title}
        </h3>
        <p className="text-[13px] md:text-[15px] lg:text-base tracking-[-0.02em] text-[#62636c] font-albert-sans leading-[1.5]">
          {description}
        </p>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────
   Data
   ───────────────────────────────────────────────────────── */

const features: PortalFeatureProps[] = [
  {
    title: "Bills Payment",
    description: "Set up automatic payments or monthly reminders",
    icon: <CreditCardIcon />,
  },
  {
    title: "Meter Consumption",
    description: "Submit meter readings and view consumption charts",
    icon: <MeterChartIcon />,
  },
  {
    title: "Invoices",
    description: "Download previous invoices instantly",
    icon: <DocumentIcon />,
  },
  {
    title: "Payment Automation",
    description: "Set up automatic payments or monthly reminders",
    icon: <RefreshCycleIcon />,
  },
  {
    title: "New connections",
    description: "Track new connection requests and support tickets",
    icon: <PlugLinkIcon />,
  },
  {
    title: "24/7 Support System",
    description: "Active customer support to report emergencies or concerns",
    icon: <HeadsetIcon />,
  },
];

/* ─────────────────────────────────────────────────────────
   Main component
   ───────────────────────────────────────────────────────── */

export function OnlinePortalSection() {
  return (
    <section className="w-full bg-[#f6f6f9] overflow-hidden py-12 md:py-16 lg:py-[100px] px-5 md:px-10 lg:px-[180px]">
      <div className="w-full max-w-[1368px] mx-auto">
        <Reveal direction="up">
          <div className="rounded-[12px] md:rounded-[24px] bg-white overflow-hidden p-4 md:p-8 lg:p-20 flex flex-col gap-10 lg:gap-16">
            {/* Header */}
            <Reveal direction="up" delay={100}>
              <div className="w-full flex flex-col lg:flex-row items-start lg:items-start justify-between gap-6 lg:gap-5">
                <div className="flex flex-col gap-4 md:gap-5 flex-1 max-w-[825px]">
                  <span className="inline-block w-fit px-4 py-1.5 rounded-full bg-[#f24d2b]/10 text-[#f24d2b] text-[13px] md:text-[15px] font-semibold tracking-[-0.02em] uppercase font-albert-sans">
                    Online Portal
                  </span>
                  <h2 className="text-[24px] md:text-[40px] lg:text-[54px] tracking-[-0.04em] font-bold text-[#122d63] font-albert-sans leading-[1.1]">
                    Smart, Simple, Secure.
                    <br />
                    Your Online Energy Portal
                  </h2>
                </div>

                {/* QR Code */}
                <div className="flex items-start justify-end">
                  <img
                    src={qrCodeImage}
                    alt="Download app QR code"
                    className="w-[120px] h-[120px] lg:w-[164px] lg:h-[164px] object-cover"
                    loading="lazy"
                  />
                </div>
              </div>
            </Reveal>

            {/* Features Grid */}
            <Reveal direction="up" delay={200}>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                {features.map((feature, index) => (
                  <Reveal key={feature.title} direction="up" delay={250 + index * 80}>
                    <PortalFeature {...feature} />
                  </Reveal>
                ))}
              </div>
            </Reveal>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
